import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Plus, X, CheckCircle2 } from 'lucide-react'
import managementService from '../../services/managementService'
import userService from '../../services/userService'
import { DataTable, HealthBadge, Loading, Unavailable } from './ManagementWidgets'
import { formatDate, formatDateTime, getErrorMessage, humanize } from './managementUtils'

export const RISK_CATEGORIES = ['Financial', 'Sales', 'Customer', 'Project delivery', 'Resource', 'Technology', 'Infrastructure', 'Compliance', 'Vendor', 'Operational', 'Strategic']
export const RISK_LEVELS = ['Low', 'Medium', 'High', 'Critical']
export const RISK_SEVERITIES = ['Red', 'Amber', 'Green', 'Grey']
export const RISK_STATUSES = ['Open', 'Mitigating', 'Closed']
export const ACTION_PRIORITIES = ['High', 'Medium', 'Low']
export const ACTION_OPEN_STATUSES = ['Open', 'In Progress', 'Overdue']

const GAP_ROUTES = {
  PROJECTS: '/projects',
  SUBSCRIPTIONS: '/subscriptions/list',
  SALES: '/management/reports/customer-collection',
  CRM: '/management/reports/sales-pipeline',
  TIMESHEETS: '/timesheets',
  ACCOUNTS: '/accounts?tab=reports'
}

const notifyError = (err, fallback) => {
  if (!err?._errorHandled) toast.error(getErrorMessage(err, fallback))
}

// ---------- users for owner pickers ----------

let usersCache = null

export const useCompanyUsers = () => {
  const [users, setUsers] = useState(usersCache || [])
  useEffect(() => {
    if (usersCache) return undefined
    let cancelled = false
    userService.getAllUsers({ limit: 500, status: 'Active' })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.users) ? res.data.users : []
        usersCache = list.map((u) => ({ id: u._id || u.id, name: u.name || u.userName || u.userEmail || u.email })).filter((u) => u.id)
        if (!cancelled) setUsers(usersCache)
      })
      .catch(() => { /* owner picker is optional */ })
    return () => { cancelled = true }
  }, [])
  return users
}

const OwnerSelect = ({ value, onChange, users }) => (
  users.length ? (
    <select className="form-select form-select-sm" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">Unassigned</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
  ) : (
    <input className="form-control form-control-sm" disabled placeholder="User list unavailable" />
  )
)

// ---------- risks ----------

const EMPTY_RISK = { title: '', category: 'Financial', impact: 'Medium', probability: 'Medium', severity: 'Amber', owner: '', targetDate: '', mitigation: '', escalationRequired: false }

export const RiskForm = ({ onSaved, onCancel, users, sourceModule = 'MANAGEMENT_MIS', sourceRef = '' }) => {
  const [form, setForm] = useState(EMPTY_RISK)
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }))

  const submit = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) return toast.warn('Risk title is required')
    setSaving(true)
    try {
      await managementService.createRisk({ ...form, sourceModule, sourceRef })
      toast.success('Risk recorded')
      setForm(EMPTY_RISK)
      onSaved?.()
    } catch (err) {
      notifyError(err, 'Could not save risk')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="mis-form no-print" onSubmit={submit}>
      <div className="mis-form-wide">
        <label>Risk title *</label>
        <input className="form-control form-control-sm" value={form.title} onChange={set('title')} maxLength={300} required />
      </div>
      <div><label>Category</label>
        <select className="form-select form-select-sm" value={form.category} onChange={set('category')}>
          {RISK_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div><label>Impact</label>
        <select className="form-select form-select-sm" value={form.impact} onChange={set('impact')}>
          {RISK_LEVELS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div><label>Probability</label>
        <select className="form-select form-select-sm" value={form.probability} onChange={set('probability')}>
          {RISK_LEVELS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div><label>Severity</label>
        <select className="form-select form-select-sm" value={form.severity} onChange={set('severity')}>
          {RISK_SEVERITIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div><label>Owner</label><OwnerSelect value={form.owner} onChange={set('owner')} users={users} /></div>
      <div><label>Target date</label>
        <input type="date" className="form-control form-control-sm" value={form.targetDate} onChange={set('targetDate')} />
      </div>
      <div className="mis-form-wide"><label>Mitigation</label>
        <textarea className="form-control form-control-sm" rows={2} value={form.mitigation} onChange={set('mitigation')} maxLength={4000} />
      </div>
      <div className="form-check" style={{ alignSelf: 'center' }}>
        <input id="risk-escalate" type="checkbox" className="form-check-input" checked={form.escalationRequired} onChange={set('escalationRequired')} />
        <label htmlFor="risk-escalate" className="form-check-label" style={{ textTransform: 'none' }}>Escalation required</label>
      </div>
      <div className="mis-form-actions">
        {onCancel && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add risk'}</button>
      </div>
    </form>
  )
}

export const RiskPanel = ({ can, onChanged, sourceModule, statusFilter }) => {
  const users = useCompanyUsers()
  const [risks, setRisks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const canEdit = can('CREATE_ACTION')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await managementService.listRisks(statusFilter ? { status: statusFilter } : {})
      setRisks(res?.data || [])
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load risks'))
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => { load() }, [load])

  const refresh = () => { load(); onChanged?.() }

  const updateStatus = async (risk, status) => {
    try {
      await managementService.updateRisk(risk._id, { status })
      toast.success(`Risk marked ${status}`)
      refresh()
    } catch (err) {
      notifyError(err, 'Could not update risk')
    }
  }

  return (
    <div>
      {canEdit && (
        <div className="mis-section-actions no-print" style={{ marginBottom: 10 }}>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowForm((s) => !s)}>
            {showForm ? <><X size={14} /> Close form</> : <><Plus size={14} /> New risk</>}
          </button>
        </div>
      )}
      {showForm && canEdit && <RiskForm users={users} sourceModule={sourceModule} onSaved={() => { setShowForm(false); refresh() }} onCancel={() => setShowForm(false)} />}
      {loading ? <Loading label="Loading risks…" /> : error ? <Unavailable note={error} /> : (
        <DataTable
          rows={risks}
          emptyText="No risks recorded. Use “New risk” to add one."
          columns={[
            { key: 'title', label: 'Risk' },
            { key: 'category', label: 'Category' },
            { key: 'severity', label: 'Severity', type: 'health' },
            { key: 'impact', label: 'Impact / probability', render: (v, r) => `${v || '—'} / ${r.probability || '—'}` },
            { key: 'owner', label: 'Owner', render: (v) => v?.name || <span className="mis-muted">Unassigned</span> },
            { key: 'targetDate', label: 'Target', type: 'date' },
            { key: 'daysOpen', label: 'Days open', align: 'right' },
            { key: 'escalationRequired', label: 'Escalate', render: (v) => (v ? <HealthBadge status="Red" label="Yes" /> : '—') },
            {
              key: 'status',
              label: 'Status',
              render: (v, r) => (canEdit ? (
                <select className="form-select form-select-sm" value={v} onChange={(e) => updateStatus(r, e.target.value)} style={{ minWidth: 120 }}>
                  {RISK_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              ) : v)
            }
          ]}
        />
      )}
    </div>
  )
}

// ---------- actions ----------

const EMPTY_ACTION = { description: '', owner: '', dueDate: '', priority: 'Medium', riskId: '', sourceIssue: '' }

export const ActionForm = ({ onSaved, onCancel, users, risks = [], sourceReport = '', reviewSnapshotId }) => {
  const [form, setForm] = useState(EMPTY_ACTION)
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }))

  const submit = async (e) => {
    e.preventDefault()
    if (!form.description.trim()) return toast.warn('Action description is required')
    setSaving(true)
    try {
      await managementService.createAction({ ...form, sourceReport, reviewSnapshotId: reviewSnapshotId || undefined })
      toast.success('Action created')
      setForm(EMPTY_ACTION)
      onSaved?.()
    } catch (err) {
      notifyError(err, 'Could not create action')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="mis-form no-print" onSubmit={submit}>
      <div className="mis-form-wide"><label>Action *</label>
        <textarea className="form-control form-control-sm" rows={2} value={form.description} onChange={set('description')} maxLength={4000} required />
      </div>
      <div><label>Owner</label><OwnerSelect value={form.owner} onChange={set('owner')} users={users} /></div>
      <div><label>Due date</label>
        <input type="date" className="form-control form-control-sm" value={form.dueDate} onChange={set('dueDate')} />
      </div>
      <div><label>Priority</label>
        <select className="form-select form-select-sm" value={form.priority} onChange={set('priority')}>
          {ACTION_PRIORITIES.map((p) => <option key={p}>{p}</option>)}
        </select>
      </div>
      <div><label>Linked risk</label>
        <select className="form-select form-select-sm" value={form.riskId} onChange={set('riskId')}>
          <option value="">None</option>
          {risks.filter((r) => r.status !== 'Closed').map((r) => <option key={r._id} value={r._id}>{r.title}</option>)}
        </select>
      </div>
      <div className="mis-form-wide"><label>Issue / context</label>
        <input className="form-control form-control-sm" value={form.sourceIssue} onChange={set('sourceIssue')} />
      </div>
      <div className="mis-form-actions">
        {onCancel && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Create action'}</button>
      </div>
    </form>
  )
}

const CloseActionForm = ({ action, onDone, onCancel }) => {
  const [status, setStatus] = useState('Completed')
  const [closureComment, setComment] = useState('')
  const [closureEvidence, setEvidence] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (!closureComment.trim()) return toast.warn('A closure comment is required')
    setSaving(true)
    try {
      await managementService.closeAction(action._id, { status, closureComment, closureEvidence })
      toast.success(`Action ${status.toLowerCase()}`)
      onDone?.()
    } catch (err) {
      notifyError(err, 'Could not close action')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="mis-form no-print" onSubmit={submit}>
      <div className="mis-form-wide"><strong>Close action:</strong> {action.description}</div>
      <div><label>Outcome</label>
        <select className="form-select form-select-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option>Completed</option>
          <option>Cancelled</option>
        </select>
      </div>
      <div className="mis-form-wide"><label>Closure comment *</label>
        <textarea className="form-control form-control-sm" rows={2} value={closureComment} onChange={(e) => setComment(e.target.value)} required />
      </div>
      <div className="mis-form-wide"><label>Evidence (link or reference)</label>
        <input className="form-control form-control-sm" value={closureEvidence} onChange={(e) => setEvidence(e.target.value)} />
      </div>
      <div className="mis-form-actions">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-sm btn-success" disabled={saving}>{saving ? 'Saving…' : 'Confirm close'}</button>
      </div>
    </form>
  )
}

const ACTION_FILTERS = ['Open', 'Overdue', 'Completed', 'All']

export const ActionPanel = ({ can, onChanged, sourceReport = 'MANAGEMENT_MIS', reviewSnapshotId }) => {
  const users = useCompanyUsers()
  const [actions, setActions] = useState([])
  const [risks, setRisks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [closing, setClosing] = useState(null)
  const [filter, setFilter] = useState('Open')
  const canCreate = can('CREATE_ACTION')
  const canClose = can('CLOSE_ACTION')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = filter === 'All' || filter === 'Open' ? {} : { status: filter }
      const [a, r] = await Promise.all([
        managementService.listActions(params),
        canCreate ? managementService.listRisks({}).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ])
      const list = a?.data || []
      setActions(filter === 'Open' ? list.filter((x) => ACTION_OPEN_STATUSES.includes(x.status)) : list)
      setRisks(r?.data || [])
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load actions'))
    } finally {
      setLoading(false)
    }
  }, [filter, canCreate])

  useEffect(() => { load() }, [load])

  const refresh = () => { load(); onChanged?.() }

  const setInProgress = async (action) => {
    try {
      await managementService.updateAction(action._id, { status: 'In Progress' })
      refresh()
    } catch (err) {
      notifyError(err, 'Could not update action')
    }
  }

  return (
    <div>
      <div className="mis-section-actions no-print" style={{ marginBottom: 10, justifyContent: 'space-between' }}>
        <div className="mis-segmented">
          {ACTION_FILTERS.map((f) => (
            <button key={f} type="button" className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
        {canCreate && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowForm((s) => !s)}>
            {showForm ? <><X size={14} /> Close form</> : <><Plus size={14} /> New action</>}
          </button>
        )}
      </div>
      {showForm && canCreate && (
        <ActionForm users={users} risks={risks} sourceReport={sourceReport} reviewSnapshotId={reviewSnapshotId} onSaved={() => { setShowForm(false); refresh() }} onCancel={() => setShowForm(false)} />
      )}
      {closing && <CloseActionForm action={closing} onCancel={() => setClosing(null)} onDone={() => { setClosing(null); refresh() }} />}
      {loading ? <Loading label="Loading actions…" /> : error ? <Unavailable note={error} /> : (
        <DataTable
          rows={actions}
          emptyText={filter === 'Open' ? 'No open actions.' : 'No actions for this filter.'}
          columns={[
            { key: 'description', label: 'Action' },
            { key: 'owner', label: 'Owner', render: (v) => v?.name || <span className="mis-muted">Unassigned</span> },
            { key: 'dueDate', label: 'Due', type: 'date' },
            { key: 'priority', label: 'Priority', type: 'health' },
            { key: 'effectiveStatus', label: 'Status', render: (v, r) => <HealthBadge status={v || r.status} label={v || r.status} /> },
            { key: 'sourceReport', label: 'Source', render: (v) => (v ? humanize(v) : '—') },
            {
              key: '_actions',
              label: '',
              render: (_, r) => {
                if (['Completed', 'Cancelled'].includes(r.status)) {
                  return <span className="mis-muted" title={r.closureComment}><CheckCircle2 size={12} /> {formatDate(r.closedAt)}</span>
                }
                return (
                  <div className="no-print" style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    {canCreate && r.status === 'Open' && (
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setInProgress(r)}>Start</button>
                    )}
                    {canClose && (
                      <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setClosing(r)}>Close</button>
                    )}
                  </div>
                )
              }
            }
          ]}
        />
      )}
    </div>
  )
}

// ---------- decisions & comments ----------

export const DecisionCommentPanel = ({ can, period, reviewSnapshotId, screenKey = 'governance' }) => {
  const [decisions, setDecisions] = useState([])
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [decisionText, setDecisionText] = useState('')
  const [decisionNote, setDecisionNote] = useState('')
  const [commentText, setCommentText] = useState('')
  const [saving, setSaving] = useState(false)
  const canRecord = can('RECORD_DECISION')
  const fromDate = period?.startDate
  const toDate = period?.endDate

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = { fromDate, toDate }
      const [d, c] = await Promise.all([managementService.listDecisions(params), managementService.listComments(params)])
      setDecisions(d?.data || [])
      setComments(c?.data || [])
    } catch (err) {
      notifyError(err, 'Could not load decisions')
    } finally {
      setLoading(false)
    }
  }, [fromDate, toDate])

  useEffect(() => { load() }, [load])

  const addDecision = async (e) => {
    e.preventDefault()
    if (!decisionText.trim()) return
    setSaving(true)
    try {
      await managementService.createDecision({ decision: decisionText, comment: decisionNote, screenKey, reviewSnapshotId: reviewSnapshotId || undefined })
      setDecisionText('')
      setDecisionNote('')
      toast.success('Decision recorded')
      load()
    } catch (err) {
      notifyError(err, 'Could not record decision')
    } finally {
      setSaving(false)
    }
  }

  const addComment = async (e) => {
    e.preventDefault()
    if (!commentText.trim()) return
    setSaving(true)
    try {
      await managementService.createComment({ comment: commentText, screenKey, reviewSnapshotId: reviewSnapshotId || undefined })
      setCommentText('')
      load()
    } catch (err) {
      notifyError(err, 'Could not add comment')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Loading label="Loading decisions…" />

  return (
    <div className="mis-two-col">
      <div>
        <h4 className="mis-subhead">Decisions ({decisions.length})</h4>
        {canRecord && (
          <form className="mis-form no-print" onSubmit={addDecision}>
            <div className="mis-form-wide"><label>Decision *</label>
              <input className="form-control form-control-sm" value={decisionText} onChange={(e) => setDecisionText(e.target.value)} maxLength={4000} />
            </div>
            <div className="mis-form-wide"><label>Rationale</label>
              <input className="form-control form-control-sm" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} maxLength={4000} />
            </div>
            <div className="mis-form-actions"><button type="submit" className="btn btn-sm btn-primary" disabled={saving || !decisionText.trim()}>Record decision</button></div>
          </form>
        )}
        {decisions.length ? (
          <ul className="mis-note-list">
            {decisions.map((d) => (
              <li key={d._id}>
                <strong>{d.decision}</strong>{d.comment ? ` — ${d.comment}` : ''}
                <small>{d.createdBy?.name || 'Unknown'} · {formatDateTime(d.createdAt)}{d.screenKey ? ` · ${humanize(d.screenKey)}` : ''}</small>
              </li>
            ))}
          </ul>
        ) : <Unavailable>No decisions recorded for this period.</Unavailable>}
      </div>
      <div>
        <h4 className="mis-subhead">Comments ({comments.length})</h4>
        {canRecord && (
          <form className="mis-form no-print" onSubmit={addComment}>
            <div className="mis-form-wide"><label>Comment *</label>
              <textarea className="form-control form-control-sm" rows={2} value={commentText} onChange={(e) => setCommentText(e.target.value)} maxLength={4000} />
            </div>
            <div className="mis-form-actions"><button type="submit" className="btn btn-sm btn-primary" disabled={saving || !commentText.trim()}>Add comment</button></div>
          </form>
        )}
        {comments.length ? (
          <ul className="mis-note-list">
            {comments.map((c) => (
              <li key={c._id}>
                {c.comment}
                <small>{c.createdBy?.name || 'Unknown'} · {formatDateTime(c.createdAt)}{c.screenKey ? ` · ${humanize(c.screenKey)}` : ''}</small>
              </li>
            ))}
          </ul>
        ) : <Unavailable>No comments for this period.</Unavailable>}
      </div>
    </div>
  )
}

// ---------- compliance gaps ----------

export const GapsTable = ({ gaps }) => (
  <DataTable
    rows={gaps || []}
    emptyText="No compliance gap checks available."
    rowKey={(g) => g.key}
    columns={[
      { key: 'label', label: 'Check' },
      { key: 'count', label: 'Count', format: 'number' },
      { key: 'amount', label: 'Amount', format: 'currency' },
      { key: 'severity', label: 'Status', type: 'health' },
      {
        key: 'sourceModule',
        label: 'Module',
        render: (v) => (GAP_ROUTES[v] ? <Link to={GAP_ROUTES[v]}>{humanize(String(v).toLowerCase())}</Link> : humanize(String(v || '—').toLowerCase()))
      }
    ]}
  />
)
